'use client';

import {
  createContext,
  useContext,
  useCallback,
  useState,
  type ReactNode,
} from 'react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

/**
 * Optional action surfaced as a clickable link inside the toast.
 *
 * Used by Worker D5 (CL-EMAIL) to surface a "Reconnect M365" CTA on
 * `NO_GRAPH_TOKEN` errors — see `apps/dashboard/src/lib/api/errors.ts`.
 * Kept generic so other error paths can reuse the slot.
 *
 * `href` is rendered as a plain `<a>` so the click navigates even when
 * the toast unmounts mid-transition (a Next.js `router.push` triggered
 * from a setTimeout-cleared element can drop the navigation). For
 * in-app routes (`/settings/storage`) Next.js still intercepts the
 * click and does a client-side push.
 */
interface ToastAction {
  label: string;
  href: string;
}

interface ToastOptions {
  /** Optional bold heading rendered above the message. */
  title?: string;
  /** Optional CTA button rendered to the right of the message. */
  action?: ToastAction;
  /** Override the 5s auto-dismiss; pass 0 to require manual dismiss. */
  durationMs?: number;
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  action?: ToastAction;
}

interface ToastContextValue {
  /** Returns the toast's id, for `dismiss`. */
  toast: (type: ToastType, message: string, options?: ToastOptions) => string;
  /** Removes a toast early (e.g. a warning whose cause has gone away). */
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const typeStyles: Record<ToastType, string> = {
  success: 'border-[#6B9080] bg-green-50 text-green-800',
  error: 'border-red-500 bg-alarm-wash text-red-800',
  info: 'border-assistant bg-violet-50 text-violet-800',
  warning: 'border-amber-500 bg-amber-50 text-amber-800',
};

const typeIcons: Record<ToastType, string> = {
  success: 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  error: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  info: 'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
  warning: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z',
};

const actionButtonStyles: Record<ToastType, string> = {
  success: 'border-[#6B9080] text-green-900 hover:bg-green-100',
  error: 'border-red-500 text-red-900 hover:bg-red-100',
  info: 'border-assistant text-violet-900 hover:bg-violet-100',
  warning: 'border-amber-500 text-amber-900 hover:bg-amber-100',
};

const DEFAULT_DURATION_MS = 5000;
/** Bumped duration for action toasts so the user has time to read + click. */
const ACTION_DURATION_MS = 9000;

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (type: ToastType, message: string, options?: ToastOptions) => {
      const id = `toast_${++toastCounter}`;
      const entry: Toast = {
        id,
        type,
        message,
        title: options?.title,
        action: options?.action,
      };
      setToasts((prev) => [...prev.slice(-4), entry]);

      // 0 means "no auto-dismiss"; otherwise honour the explicit duration
      // (or the action-aware default).
      const duration =
        options?.durationMs ??
        (options?.action ? ACTION_DURATION_MS : DEFAULT_DURATION_MS);
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }
      return id;
    },
    [],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast, dismiss: removeToast }}>
      {children}
      <div className="fixed right-4 top-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-start gap-3 rounded-md border-l-4 px-4 py-3 shadow-md animate-in slide-in-from-right ${typeStyles[t.type]}`}
            role="alert"
          >
            <svg
              className="mt-0.5 h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={typeIcons[t.type]} />
            </svg>
            <div className="flex-1 min-w-0">
              {t.title && (
                <p className="text-sm font-semibold leading-tight">{t.title}</p>
              )}
              <p className={`text-sm ${t.title ? 'mt-0.5' : ''}`}>{t.message}</p>
              {t.action && (
                <a
                  href={t.action.href}
                  onClick={() => removeToast(t.id)}
                  className={`mt-2 inline-flex items-center rounded-md border bg-surface/60 px-2.5 py-1 text-xs font-medium transition-colors ${actionButtonStyles[t.type]}`}
                  data-testid="toast-action"
                >
                  {t.action.label}
                </a>
              )}
            </div>
            <button
              onClick={() => removeToast(t.id)}
              className="text-current opacity-50 hover:opacity-100"
              aria-label="Dismiss"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const noopToast: ToastContextValue = { toast: () => '', dismiss: () => {} };

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  return context ?? noopToast;
}
